import { Shield, AlertTriangle } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";
import AIReadabilityBlock from "@/components/AIReadabilityBlock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription } from "@/components/ui/alert";

const DeportationRemovalDefense = () => {
  const faqs = [
    {
      question: "What should I do if I receive an NTA?",
      answer: "Contact an immigration attorney immediately. A Notice to Appear (NTA) starts formal removal proceedings. Don't ignore it or miss your court date. We can help you understand the charges, build your defense, and represent you in court.",
    },
    {
      question: "Can I get a bond during removal proceedings?",
      answer: "In many cases, yes. We can request a bond hearing before an immigration judge. If granted, you can be released from detention while your case proceeds. We'll fight for the lowest possible bond and prepare you for the bond hearing.",
    },
  ];

  const mainContent = (
    <>
      <Alert variant="destructive" className="mb-8">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="font-semibold">
          Facing Deportation or Detention? Contact us immediately at (832) 930-3059. Time is critical.
        </AlertDescription>
      </Alert>

      <div className="prose prose-lg max-w-none mb-12">
        <h2 className="text-3xl font-bold mb-6">Stop Deportation</h2>
        <p className="text-xl text-muted-foreground mb-8">
          Receiving a notice to appear in immigration court can be terrifying. Whether you're facing deportation for overstaying a visa, a criminal conviction, or any other reason, your future in the U.S. is at stake. At Daniel Albert Law Firm, we fight aggressively to keep families together and defend your right to remain in the United States.
        </p>

        <h3 className="text-2xl font-bold mb-4">Defense Strategies</h3>
        <ul className="list-disc pl-6 mb-6">
          <li>Cancellation of removal</li>
          <li>Asylum applications</li>
          <li>Adjustment of status</li>
          <li>Waivers of inadmissibility</li>
          <li>Voluntary departure</li>
          <li>Appeals to the Board of Immigration Appeals (BIA)</li>
          <li>Motions to reopen or reconsider</li>
        </ul>

        <h3 className="text-2xl font-bold mb-4">Our Approach</h3>
        <p className="mb-8">
          Time is critical in removal proceedings. We act quickly to analyze your case, identify defenses, prepare compelling evidence, represent you in immigration court, and pursue appeals if necessary. Contact us promptly for urgent deportation matters.
        </p>

        <h3 className="text-2xl font-bold mb-6">Frequently Asked Questions</h3>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </>
  );

  const aiKeyPoints = [
    "Aggressive defense against deportation and removal proceedings",
    "Cancellation of removal applications",
    "Bond hearings to get released from detention",
    "Asylum applications during removal proceedings",
    "Adjustment of status defenses",
    "Waivers of inadmissibility",
    "Appeals to the Board of Immigration Appeals (BIA)",
    "Motions to reopen or reconsider",
    "Voluntary departure options",
    "Emergency deportation defense - immediate assistance available"
  ];

  const aiFaqs = faqs.map(f => ({ question: f.question, answer: f.answer }));

  return (
    <>
      <AIReadabilityBlock
        serviceName="Deportation & Removal Defense"
        serviceDescription="Aggressive deportation defense attorney in Houston fighting to stop removal proceedings, secure bond release, and keep families together through all available legal defenses."
        keyPoints={aiKeyPoints}
        faqs={aiFaqs}
        areaServed="Houston, Texas"
      />
      <GenericPracticePage
        title="Deportation & Removal Defense"
        subtitle="Aggressive defense against deportation and removal proceedings"
        icon={<Shield className="h-20 w-20 mx-auto mb-6 text-accent" />}
        mainContent={mainContent}
      />
    </>
  );
};

export default DeportationRemovalDefense;
